import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  attestSoaVersion,
  generateSoaVersion,
  getSoaVersion,
  listSoaEntries,
  listSoaVersions,
  publishSoaVersion,
  updateSoaEntry,
  type SoaApplicability,
  type SoaEntryView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

/**
 * The SoA is generated from the live register, never hand-maintained — a
 * spreadsheet drifting from reality is a classic finding. Releases freeze the
 * whole table, get signed off, and publish.
 */
export function ComplianceSoaPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "unresolved" | "excluded">("all");
  const [openEntry, setOpenEntry] = useState<SoaEntryView | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: entries, isLoading } = useQuery({ queryKey: ["compliance-soa"], queryFn: listSoaEntries });
  const { data: versions } = useQuery({ queryKey: ["soa-versions"], queryFn: listSoaVersions });

  const stats = useMemo(() => {
    const all = entries ?? [];
    return {
      total: all.length,
      applicable: all.filter((e) => e.entry.applicability === "applicable").length,
      excluded: all.filter((e) => e.entry.applicability === "not_applicable").length,
      unresolved: all.filter((e) => e.blockers.length > 0).length,
    };
  }, [entries]);

  const shown = (entries ?? []).filter((e) =>
    filter === "all"
      ? true
      : filter === "unresolved"
        ? e.blockers.length > 0
        : e.entry.applicability === "not_applicable",
  );

  const draft = versions?.find((v) => !v.publishedAt) ?? null;
  const published = versions?.find((v) => v.publishedAt) ?? null;

  const generate = useMutation({
    mutationFn: generateSoaVersion,
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["soa-versions"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div>
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Statement of Applicability</h1>
          <p className="mt-2 text-text-secondary">
            All 93 Annex A controls, each included or excluded with a justification, and the
            controls that implement them. Generated from the register, then frozen and signed.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading"
          >
            <option value="all">All {stats.total}</option>
            <option value="unresolved">Unresolved {stats.unresolved}</option>
            <option value="excluded">Excluded {stats.excluded}</option>
          </select>
          {draft ? (
            <button
              onClick={() => setReleaseOpen(true)}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            >
              Release v{draft.version}
            </button>
          ) : (
            <button
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              title="Freeze the current register into a new draft release"
            >
              Generate release
            </button>
          )}
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-token-lg border border-border bg-bg-card px-4 py-2.5 text-xs">
        <span className="text-text-muted">
          <span className="text-text-heading">{stats.applicable}</span> applicable ·{" "}
          <span className="text-text-heading">{stats.excluded}</span> excluded ·{" "}
          <span className={stats.unresolved > 0 ? "text-warning" : "text-success"}>
            {stats.unresolved}
          </span>{" "}
          unresolved
        </span>
        <span className="text-text-muted">
          {published
            ? `Released: v${published.version} on ${new Date(published.publishedAt!).toLocaleDateString()}`
            : "No release published yet"}
          {draft && ` · v${draft.version} drafted, awaiting sign-off`}
        </span>
      </div>

      {error && <p className="mb-3 text-xs text-danger">{error}</p>}

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Annex A control</th>
                <th className="px-4 py-2.5 font-semibold">Applicable</th>
                <th className="px-4 py-2.5 font-semibold">Implemented by</th>
                <th className="px-4 py-2.5 font-semibold">Risks</th>
                <th className="px-4 py-2.5 font-semibold">Justification</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr
                  key={e.entry.id}
                  className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                  onClick={() => setOpenEntry(e)}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">{e.ref}</td>
                  <td className="px-4 py-2.5 text-text-secondary">{e.title}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <ApplicabilityPill value={e.entry.applicability} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-text-muted">
                    {e.controls.map((c) => c.ref).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-text-muted">
                    {e.riskRefs.join(", ") || "—"}
                  </td>
                  <td className="max-w-md truncate px-4 py-2.5 text-text-secondary">
                    {e.blockers.length > 0 ? (
                      <span className="text-warning">{e.blockers[0].message}</span>
                    ) : (
                      e.entry.justification
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openEntry && <EntryModal entry={openEntry} onClose={() => setOpenEntry(null)} />}
      {releaseOpen && draft && (
        <ReleaseModal versionId={draft.id} version={draft.version} onClose={() => setReleaseOpen(false)} />
      )}
    </div>
  );
}

function ApplicabilityPill({ value }: { value: SoaApplicability }) {
  const map: Record<SoaApplicability, { label: string; cls: string }> = {
    undecided: { label: "Undecided", cls: "bg-warning/15 text-warning" },
    applicable: { label: "Yes", cls: "bg-success/15 text-success" },
    not_applicable: { label: "Excluded", cls: "border border-border text-text-secondary" },
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${map[value].cls}`}>
      {map[value].label}
    </span>
  );
}

function EntryModal({ entry, onClose }: { entry: SoaEntryView; onClose: () => void }) {
  const qc = useQueryClient();
  const [applicability, setApplicability] = useState<SoaApplicability>(entry.entry.applicability);
  const [justification, setJustification] = useState(entry.entry.justification ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => updateSoaEntry(entry.entry.id, { applicability, justification: justification || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-soa"] });
      void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const suggested =
    entry.riskRefs.length > 0
      ? `Included to treat ${entry.riskRefs.join(", ")}, implemented by ${entry.controls.map((c) => c.ref).join(", ")}.`
      : entry.controls.length > 0
        ? `Included; implemented by ${entry.controls.map((c) => c.ref).join(", ")}.`
        : null;

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title={`${entry.ref} — ${entry.title}`}
      wide
      footer={
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Save
        </button>
      }
    >
      <div className="space-y-4 text-sm">
        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            Applicability
          </h3>
          <select
            value={applicability}
            onChange={(e) => setApplicability(e.target.value as SoaApplicability)}
            className={inputCls}
          >
            <option value="undecided">Undecided</option>
            <option value="applicable">Applicable — we implement this</option>
            <option value="not_applicable">Excluded — not applicable to our scope</option>
          </select>
        </div>

        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            Justification
          </h3>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            placeholder={
              applicability === "not_applicable"
                ? "Why this doesn't apply — argued from scope, e.g. “fully remote, no offices in scope”."
                : "Why this is included — the risks it treats, or the legal/contractual driver."
            }
            className={inputCls}
          />
          {suggested && applicability !== "not_applicable" && (
            <button
              onClick={() => setJustification(suggested)}
              className="mt-1.5 text-xs text-accent hover:underline"
            >
              Use: “{suggested}”
            </button>
          )}
        </div>

        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            Implemented by
          </h3>
          {entry.controls.length === 0 ? (
            <p className="text-text-muted">
              No control maps to this reference. Map one in the control library, or exclude it here
              with a justification.
            </p>
          ) : (
            <ul className="space-y-1">
              {entry.controls.map((c) => (
                <li key={c.id} className="text-text-secondary">
                  <span className="font-medium text-text-heading">{c.ref}</span> {c.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {entry.riskRefs.length > 0 && (
          <div>
            <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
              Treats risks
            </h3>
            <p className="text-text-secondary">{entry.riskRefs.join(", ")}</p>
          </div>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function ReleaseModal({
  versionId,
  version,
  onClose,
}: {
  versionId: string;
  version: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [statement, setStatement] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["soa-version", versionId], queryFn: () => getSoaVersion(versionId) });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["soa-versions"] });
    void qc.invalidateQueries({ queryKey: ["soa-version", versionId] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
  };

  const attest = useMutation({
    mutationFn: (action: "prepared" | "reviewed" | "approved") =>
      attestSoaVersion(versionId, action, statement),
    onSuccess: () => {
      setStatement("");
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const publish = useMutation({
    mutationFn: () => publishSoaVersion(versionId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const next =
    data?.cycleStage === "none"
      ? { action: "prepared" as const, label: "Submit for review" }
      : data?.cycleStage === "prepared"
        ? { action: "reviewed" as const, label: "Review" }
        : data?.cycleStage === "reviewed"
          ? { action: "approved" as const, label: "Approve" }
          : null;

  return (
    <Modal open onClose={onClose} title={`Release SoA v${version}`} wide>
      <div className="space-y-4 text-sm">
        <p className="text-text-secondary">
          This release freezes all {(data?.version.entries as unknown[] | undefined)?.length ?? 93}{" "}
          Annex A decisions as they stand. The SoA is a controlled document, so it needs the same
          three-eyes sign-off as a policy before it can be published.
        </p>

        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            Sign-off — {data?.cycleStage ?? "…"}
          </h3>
          <ul className="space-y-1.5">
            {data?.attestations.map((a) => (
              <li key={a.id} className="text-text-secondary">
                <span className="font-medium text-text-heading">{a.actor?.name ?? "?"}</span> {a.action} ·{" "}
                {new Date(a.occurredAt).toLocaleString()}
                {a.statement && <div className="ml-4 text-xs italic text-text-muted">“{a.statement}”</div>}
              </li>
            ))}
          </ul>
        </div>

        {next && (
          <div className="flex gap-2">
            <input
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder="What are you asserting?"
              className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
            />
            <button
              onClick={() => attest.mutate(next.action)}
              disabled={!statement.trim() || attest.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {next.label}
            </button>
          </div>
        )}

        {data?.cycleStage === "approved" && (
          <button
            onClick={() => publish.mutate()}
            disabled={publish.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Publish v{version}
          </button>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
